import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { formatDate } from "../../../utils/toDate";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../../components/table-component/RoundedTable";
import PageWrapper from "../../../components/PageWrapper";
import CustomInput from "../../../components/CustomInput";
import SearchFilter from "../../../components/SearchFilter";
import CustomButton from "../../../components/CustomButton";
import InformationBox from "../../../components/InformationBox";
import PageHeadline from "../../../components/PageHeadline";
import Loader from "../../../components/Loader";
import { UserAuth } from "../../../utils/AuthContext";
import searchIcon from "../../../assets/search_icon.png";

export default function InternalInCompletePaperOrders() {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Search states
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("orderNumber"),
    value: "orderNumber",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  // Fetch orders function
  const fetchOrders = async (page = 1, searchParams = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: 5,
        organizationId: userData?.organizationId,
        isCompleted: false, // Only fetch incomplete orders
        ...searchParams,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/paper`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "organization-id": userData?.organizationId,
          },
        }
      );

      setOrders(response.data.orders || []);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalRecords: response.data.totalOrders,
      });
    } catch (err) {
      setError(err.response?.data?.error || t("fetchError"));
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, []);

  // Handle pagination change
  const handlePageChange = (newPage) => {
    fetchOrders(newPage, getSearchParams());
  };

  // Get current search parameters
  const getSearchParams = () => {
    if (selectedTitle.value === "date") {
      return {
        searchType: "date",
        searchQuery: JSON.stringify({ from: startDate, to: endDate }),
      };
    }
    return {
      searchType: selectedTitle.value,
      searchQuery: searchQuery,
    };
  };

  // Handle search
  const handleSearch = () => {
    fetchOrders(1, getSearchParams());
  };

  // Prepare table data
  const tableTitles = [
    { value: "order" },
    { value: "Date", keepOnMobile: true },
  ];

  const tableRows = orders.map((order) => [
    {
      value: order.orderNumber,
      type: TABLE_CELL_TYPES.LINK,
      to: { pathname: `/internal-paper-order-page/${order.id}` },
      bold: true,
      keepOnMobile: true,
    },
    {
      value: order.orderDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
  ]);

  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <PageHeadline
          title="incompleteInternalOrders"
          classList="u-margin--top_400"
        />

        <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end">
          <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 u-width_full-width o-column--horizontal-align_space-between">
            {selectedTitle.value === "date" ? (
              <div className="o-column-row-switcher o-column--gap-size_300">
                <CustomInput
                  name="date"
                  title={t("from")}
                  type="date"
                  thick
                  onChange={(e) => setStartDate(e.target.value)}
                  value={startDate}
                />
                <CustomInput
                  name="date"
                  title={t("to")}
                  type="date"
                  thick
                  onChange={(e) => setEndDate(e.target.value)}
                  value={endDate}
                />
              </div>
            ) : (
              <CustomInput
                placeholder={`${t("searchTitle")} ${t(selectedTitle?.value)}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                resetSearch={() => {
                  setSearchQuery("");
                  handleSearch();
                }}
                thick
                flex
              />
            )}
            <SearchFilter
              options={[
                { title: t("orderNumber"), value: "orderNumber" },
                { title: t("Date"), value: "date" },
              ]}
              selected={selectedTitle}
              setSelected={setSelectedTitle}
              thick
            />
          </div>

          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </section>

        {loading && <Loader />}

        {orders.length > 0 ? (
          <section>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
              onPageChange={handlePageChange}
            />
          </section>
        ) : (
          <InformationBox>
            {!loading && (error || t("noOrders"))}
          </InformationBox>
        )}
      </div>
    </PageWrapper>
  );
}
